import { useLayoutEffect } from 'react';
import { useNavigationType } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getLenis } from './useLenis.js';

gsap.registerPlugin(ScrollTrigger);

const scrollPositions = new Map();
let prevPath = typeof window !== 'undefined' ? window.location.pathname : '/';

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Save scroll *before* React commits a route change. By the time the old
// page's cleanup runs, AboutPinned's GSAP ctx.revert() has already torn
// down its pin spacer — that drops document height by several thousand
// pixels and the browser silently clamps window.scrollY to the new max.
// A capture-phase save catches the real position before any of that fires.
if (typeof window !== 'undefined') {
  // Track only user-initiated scrolls. Spacer teardown on unmount fires a
  // scroll event too, but it isn't preceded by wheel/touch/keydown, so we
  // can ignore it via this gate. Reset on each full route restoration.
  let userScrolling = false;
  let userScrollTimer;
  const armUserScroll = () => {
    userScrolling = true;
    clearTimeout(userScrollTimer);
    userScrollTimer = setTimeout(() => {
      userScrolling = false;
    }, 250);
  };
  window.addEventListener('wheel', armUserScroll, { passive: true });
  window.addEventListener('touchmove', armUserScroll, { passive: true });
  window.addEventListener(
    'keydown',
    (e) => {
      if (
        e.code === 'ArrowDown' ||
        e.code === 'ArrowUp' ||
        e.code === 'PageDown' ||
        e.code === 'PageUp' ||
        e.code === 'Space' ||
        e.code === 'Home' ||
        e.code === 'End'
      ) {
        armUserScroll();
      }
    },
    { passive: true }
  );
  window.addEventListener(
    'scroll',
    () => {
      if (userScrolling) {
        scrollPositions.set(window.location.pathname, window.scrollY);
      }
    },
    { passive: true }
  );

  // Synchronous save on click — runs before React Router's bubble-phase
  // onClick, so the scrollY captured here is the position the user actually
  // saw at the moment of the click, not whatever it gets clamped to once
  // the new page mounts and the old pin spacer is torn down.
  document.addEventListener(
    'click',
    () => {
      scrollPositions.set(window.location.pathname, window.scrollY);
    },
    true
  );

  // Browser back/forward: popstate fires after the URL flips but before
  // React rerenders, so window.scrollY still reflects the page being left
  // (the *old* pathname, which we tracked in prevPath).
  window.addEventListener('popstate', () => {
    scrollPositions.set(prevPath, window.scrollY);
  });
}

// Top of the element the hash names, less the fixed nav; the page top when
// there is no hash or nothing on the page has that id.
function anchorY(hash) {
  if (!hash) return 0;
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    // A malformed escape: look the id up as written.
  }
  const target = document.getElementById(id);
  if (!target) return 0;
  const navH =
    parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-h'), 10) || 72;
  return Math.max(0, target.getBoundingClientRect().top + window.scrollY - navH);
}

function scrollToY(y) {
  const lenis = getLenis();
  // Lenis re-measures the page on a debounced ResizeObserver, so right after a
  // route change its limit can still be the previous page's and clamp y.
  lenis?.resize();
  // Through ScrollTrigger's own scroll function, so the position it caches is
  // y: ScrollTrigger.refresh() records the position, scrolls to 0 to measure
  // and scrolls back to what it recorded, and GSAP 3.15 can record a cached
  // value from before a scrollTo it wasn't told about (T-030).
  ScrollTrigger.getScrollFunc(window)(y);
  lenis?.scrollTo(y, { immediate: true, force: true });
}

/**
 * Lands each URL where it should open: where the visitor left it on a browser
 * back/forward, at the element its hash names, otherwise at the top. The page
 * animations land it once the page's content is in, before they set up their
 * triggers (useAnimations), so an anchor on a lazy page is there to be found
 * and the triggers are measured from the landing.
 */
export function useScrollLanding(pathname, hash, pageAnimations) {
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    prevPath = pathname;
  }, [pathname]);

  // Runs in the commit that changes the URL, before the browser captures the
  // new view-transition snapshot; if the content is already in, the landing
  // is baked into the target.
  useLayoutEffect(() => {
    const saved = navigationType === 'POP' ? scrollPositions.get(pathname) : undefined;
    pageAnimations.landWhenReady(pathname, () => scrollToY(saved ?? anchorY(hash)));
  }, [pageAnimations, pathname, hash, navigationType]);
}
